import * as React from 'react'
import { ScrollView, Text, View } from 'react-native'
import styled from '@theme/styled-components'
import theme from '@theme/Theme'

import Button from '@components/button/Button'
import Tag from '@components/tag/Tag'
import Animators from '@components/root/Animators'
import Timeline from './Timeline'

import useI18n from '@i18n/useI18n'

import { IncidentFormScheme, IncidentFormFields } from '../scheme'

const FormLayout = ({
  steps,
  step,
  onStep,
  titleKey,
  form,
  onNext,
  onBack,
  onQuit,
  children,
  last,
  full,
  validation = () => true,
  fixed,
  data,
  loading,
}: IncidentLayoutProps) => {
  const i18n = useI18n()

  const [valid, setValid] = React.useState(false)

  const fields = React.useMemo(() => IncidentFormFields[step].map((key) => form[key]), [form, step])

  React.useEffect(() => {
    const schema = IncidentFormScheme[step as keyof typeof IncidentFormScheme]

    if (schema) {
      schema
        .validate(form)
        .then(() => setValid(validation(form)))
        .catch(() => setValid(false))
    }
  }, [fields, step, data])

  return (
    <MainContainer>
      <TopContainer>
        <TopLine>
          <SubTitle>{i18n.t('screens.incident.START.title')}</SubTitle>

          <Tag
            activeColor
            focused
            item={{ label: i18n.t('screens.incident.quit'), value: 'tag' }}
            selected={false}
            leftIcon="close"
            onItemSelected={onQuit}
          />
        </TopLine>

        <Timeline items={steps} selected={step} onItemSelected={onStep} />
      </TopContainer>

      <DualLine>
        <Column>
          <Animators>
            <Title>{i18n.t(`screens.incident.${titleKey || step}.title`, form)}</Title>
          </Animators>
        </Column>

        <Column full={full}>
          {fixed ? (
            children
          ) : (
            <ScrollView
              scrollIndicatorInsets={{ right: 1 }}
              contentContainerStyle={{ flexGrow: 1 }}
              bounces={false}
              keyboardShouldPersistTaps="handled">
              {children}
            </ScrollView>
          )}
        </Column>
      </DualLine>

      <Buttons>
        <ButtonContainer>
          <Button
            label={i18n.t('screens.incident.previous')}
            status="active"
            onClick={onBack}
            textOnly
            color="light"
            textColor={theme.colors.primaryText}
          />
        </ButtonContainer>
        <ButtonContainer>
          <Button
            label={i18n.t(`screens.incident.${last ? 'declare' : 'next'}`)}
            status={loading ? 'loading' : valid ? 'active' : 'disabled'}
            onClick={onNext}
          />
        </ButtonContainer>
      </Buttons>
    </MainContainer>
  )
}

export default FormLayout

const MainContainer = styled(View)`
  flex: 1;
  flex-direction: column;
`

const TopContainer = styled(View)`
  padding: 25px;
`

const TopLine = styled(View)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 25px;
`

const DualLine = styled(View)`
  flex: 1;
  flex-direction: row;
`

const Column = styled(View)<{ left?: boolean; full?: boolean }>`
  flex: 1;
  flex-direction: column;
  background-color: ${(props) => (props.left ? props.theme.colors.cardSecondary : props.theme.colors.background)};
  padding: ${(props) => (props.full ? 0 : 25)}px;
  position: relative;
`

const SubTitle = styled(Text)`
  ${(props) => props.theme.fonts.Title2};
  color: ${(props) => props.theme.colors.secondaryText};
  font-size: 20px;
`

const Title = styled(Text)`
  ${(props) => props.theme.fonts.Heading1};
`

const Buttons = styled(View)`
  flex-direction: row;
  justify-content: center;
  background-color: ${(props) => props.theme.colors.background};
`

const ButtonContainer = styled(View)`
  width: 275px;
  margin: 12px;
`
